import { useCallback, useEffect, useMemo, useState } from "react";

export const WEBSITE_LEADS_COLUMNS_STORAGE_KEY = "cms_website_leads_columns";
export const MY_WEBSITE_LEADS_COLUMNS_STORAGE_KEY = "cms_my_website_leads_columns";

export function buildWebsiteLeadsColumns(canAssign = false) {
  return [
    ...(canAssign
      ? [
          {
            key: "select",
            title: "",
            dataKey: null,
            hideable: false,
            defaultVisible: true,
          },
        ]
      : []),
    { key: "id", title: "#", dataKey: "id", hideable: false, defaultVisible: true },
    { key: "name", title: "Name", dataKey: "name", defaultVisible: true },
    { key: "email", title: "Email", dataKey: "email", defaultVisible: true },
    { key: "phone", title: "Contact", dataKey: "phone", defaultVisible: true },
    { key: "country", title: "Country", dataKey: "country", defaultVisible: false },
    {
      key: "destination",
      title: "Destination",
      dataKey: "destination",
      defaultVisible: false,
    },
    { key: "inquiry", title: "Inquiry", dataKey: "inquiry", defaultVisible: false },
    { key: "type", title: "Form Type", dataKey: "type", defaultVisible: true },
    ...(canAssign
      ? [
          {
            key: "assigned_user_id",
            title: "Assigned To",
            dataKey: "assigned_user_id",
            defaultVisible: true,
          },
        ]
      : []),
    {
      key: "created_at",
      title: "Created at",
      dataKey: "created_at",
      defaultVisible: false,
    },
    { key: "actions", title: "Actions", dataKey: null, hideable: false, defaultVisible: true },
  ];
}

export const MY_WEBSITE_LEADS_COLUMNS = [
  { key: "id", title: "#", dataKey: "id", hideable: false, defaultVisible: true },
  { key: "name", title: "Name", dataKey: "name", defaultVisible: true },
  { key: "email", title: "Email", dataKey: "email", defaultVisible: true },
  { key: "phone", title: "Contact", dataKey: "phone", defaultVisible: true },
  { key: "country", title: "Country", dataKey: "country", defaultVisible: false },
  {
    key: "destination",
    title: "Destination",
    dataKey: "destination",
    defaultVisible: false,
  },
  { key: "inquiry", title: "Inquiry", dataKey: "inquiry", defaultVisible: false },
  { key: "type", title: "Form Type", dataKey: "type", defaultVisible: true },
  { key: "notes", title: "Notes", dataKey: "notes", defaultVisible: false },
  {
    key: "created_at",
    title: "Created at",
    dataKey: "created_at",
    defaultVisible: false,
  },
  {
    key: "assigned_at",
    title: "Assigned at",
    dataKey: "assigned_at",
    defaultVisible: false,
  },
  { key: "actions", title: "Actions", dataKey: null, hideable: false, defaultVisible: true },
];

function buildDefaultVisibility(columns) {
  return columns.reduce((acc, column) => {
    acc[column.key] =
      column.hideable === false ? true : column.defaultVisible !== false;
    return acc;
  }, {});
}

function loadVisibility(storageKey, columns) {
  const defaults = buildDefaultVisibility(columns);
  try {
    const saved = localStorage.getItem(storageKey);
    if (!saved) return defaults;
    const parsed = JSON.parse(saved);
    return { ...defaults, ...parsed };
  } catch {
    return defaults;
  }
}

export function useLeadsColumnSettings(storageKey, columns) {
  const [visibility, setVisibility] = useState(() =>
    loadVisibility(storageKey, columns),
  );

  useEffect(() => {
    setVisibility(loadVisibility(storageKey, columns));
  }, [storageKey, columns]);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(visibility));
  }, [storageKey, visibility]);

  const visibleColumns = useMemo(
    () => columns.filter((column) => visibility[column.key] !== false),
    [columns, visibility],
  );

  const toggleColumn = useCallback((key) => {
    const column = columns.find((item) => item.key === key);
    if (!column || column.hideable === false) return;
    setVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  }, [columns]);

  const setColumnVisibility = useCallback((key, visible) => {
    const column = columns.find((item) => item.key === key);
    if (!column || column.hideable === false) return;
    setVisibility((prev) => ({ ...prev, [key]: visible }));
  }, [columns]);

  const resetToDefaults = useCallback(() => {
    setVisibility(buildDefaultVisibility(columns));
  }, [columns]);

  const hideableColumns = useMemo(
    () => columns.filter((column) => column.hideable !== false),
    [columns],
  );

  return {
    visibility,
    visibleColumns,
    hideableColumns,
    toggleColumn,
    setColumnVisibility,
    resetToDefaults,
  };
}
